import React, { FormEvent } from "react";
import {
    PaymentElement,
    useStripe,
    useElements,
    Elements,
} from "@stripe/react-stripe-js";
import { LoadingButton } from "@mui/lab";
import { LocalCurrency } from "../common/LocalCurrency";
import { useGlobalAlertsSubject } from "../common/GlobalAlerts";
import { AlertColor } from "@mui/material";
import { tapLog } from "../../utils/testing/tapLog";
import { pipe } from "fp-ts/function";
import { loadStripe } from "@stripe/stripe-js";
import { CenteredProgress } from "../common/CenteredProgress";
import { CartItem } from "../../domain/payment";

// todo stripe is loaded on transfer payment as well... this code should move to render somehow, but executed only once
const stripePromise = loadStripe(
    // @ts-ignore
    process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
);

interface Props extends FormProps {
    items: Array<CartItem>;
}

export const StripeCheckout = (p: Props) => {
    const [clientSecret, setClientSecret] = React.useState<string | null>(null);

    React.useEffect(() => {
        fetch("/api/stripe-payment-intent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                items: p.items,
            }),
        })
            .then((res) => res.json())
            .then((data) => setClientSecret(data.clientSecret));
    }, []);

    return clientSecret == null ? (
        <CenteredProgress sx={{ height: "100%", width: "100%" }} />
    ) : (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret: clientSecret,
            }}
        >
            <StripeCheckoutForm amount={p.amount} eventId={p.eventId} />
        </Elements>
    );
};

interface FormProps {
    amount: number;
    eventId: number;
}

const StripeCheckoutForm = (p: FormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const alerts$ = useGlobalAlertsSubject();

    const setMessage = (message: string, severity: AlertColor = "error") => {
        alerts$.next({
            severity,
            message,
        });
    };

    const [isLoading, setIsLoading] = React.useState(false);

    React.useEffect(() => {
        if (!stripe) {
            return;
        }

        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent?.status) {
                case "succeeded":
                    setMessage("Payment succeeded!", "success");
                    break;
                case "processing":
                    setMessage("Your payment is processing.", "info");
                    break;
                case "requires_payment_method":
                    setMessage(
                        "Your payment was not successful, please try again."
                    );
                    break;
                default:
                    setMessage("Something went wrong.");
                    break;
            }
        });
    }, [stripe]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        const port =
            window.location.port === null ? "" : `:${window.location.port}`;

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: pipe(
                    window.location.protocol +
                        "//" +
                        window.location.hostname +
                        port +
                        `/event/${p.eventId}/thank-you`,
                    tapLog("return_url")
                ),
            },
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === "card_error" || error.type === "validation_error") {
            setMessage(error.message ?? "card error");
        } else {
            setMessage("An unexpected error occurred.");
        }

        setIsLoading(false);
    };

    return (
        <form id="payment-form" onSubmit={handleSubmit}>
            <PaymentElement
                id="payment-element"
                options={{
                    layout: "auto",
                }}
            />
            <LoadingButton
                fullWidth
                loading={isLoading}
                variant={"contained"}
                disabled={isLoading || !stripe || !elements}
                id="submit"
                type="submit"
                sx={{ mt: 2 }}
            >
                <span>
                    zaplatit <LocalCurrency value={p.amount} currency={"czk"} />
                </span>
            </LoadingButton>
        </form>
    );
};
